import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Trophy, Star, Flame, Crown, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  points: number;
  unlocked_at?: string;
  progress?: number;
  requirement_value: number;
}

export function AchievementsDisplay() {
  const [recentAchievements, setRecentAchievements] = useState<Achievement[]>([]);
  const [streaks, setStreaks] = useState<Record<string, number>>({});
  const [totalPoints, setTotalPoints] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAchievements();
  }, []);

  const loadAchievements = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch recent achievements (last 5)
      const { data: userAchievements } = await supabase
        .from('user_achievements')
        .select(`
          *,
          achievement:achievements(*)
        `)
        .eq('user_id', user.id)
        .order('unlocked_at', { ascending: false })
        .limit(5);

      // Fetch streaks
      const { data: streakData } = await supabase
        .from('user_streaks' as any)
        .select('*')
        .eq('user_id', user.id);

      // Calculate total points
      const { data: allAchievements } = await supabase
        .from('user_achievements')
        .select(`
          achievement:achievements(points)
        `)
        .eq('user_id', user.id);

      const points = allAchievements?.reduce((sum, ua: any) => 
        sum + (ua.achievement?.points || 0), 0) || 0;

      const streakMap: Record<string, number> = {};
      (streakData as any)?.forEach((s: any) => {
        streakMap[s.streak_type] = s.current_streak;
      });

      setRecentAchievements(userAchievements?.map((ua: any) => ({
        ...ua.achievement,
        unlocked_at: ua.unlocked_at,
        progress: ua.progress
      })) || []);
      
      setStreaks(streakMap);
      setTotalPoints(points);
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'platinum': return 'from-accent/30 to-primary/20 border-accent/50';
      case 'gold': return 'from-secondary/20 to-secondary/30 border-secondary/50';
      case 'silver': return 'from-muted/20 to-muted/30 border-muted/50';
      default: return 'from-accent/20 to-accent/30 border-accent/50';
    }
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'platinum': return 'bg-accent/10 text-accent border-accent/30';
      case 'gold': return 'bg-secondary/10 text-secondary border-secondary/30';
      case 'silver': return 'bg-muted/10 text-muted-foreground border-muted/30';
      default: return 'bg-accent/10 text-accent border-accent/30';
    }
  };

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-6 bg-muted rounded w-1/3" />
        </CardHeader>
        <CardContent>
          <div className="h-24 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  if (recentAchievements.length === 0 && Object.keys(streaks).length === 0) {
    return null; // Don't show if no achievements yet
  }

  return (
    <Card className="border-2 border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            Your Progress
          </div>
          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-base">
            <Star className="w-4 h-4 mr-1" />
            {totalPoints} points
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Active Streaks */}
        {Object.keys(streaks).length > 0 && (
          <div className="space-y-2">
            <div className="text-base font-semibold flex items-center gap-2">
              <Flame className="w-5 h-5 text-accent" />
              Active Streaks
            </div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(streaks).slice(0, 4).map(([type, count]) => (
                <div key={type} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <div className="text-sm text-muted-foreground capitalize">
                      {type.replace('_', ' ')}
                    </div>
                    <div className="text-lg font-bold">{count} days</div>
                  </div>
                  <Flame className="w-5 h-5 text-accent" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Achievements */}
        {recentAchievements.length > 0 && (
          <div className="space-y-2">
            <div className="text-base font-semibold">Recent Achievements</div>
            <div className="space-y-2">
              {recentAchievements.slice(0, 3).map((achievement) => (
                <div
                  key={achievement.id}
                  className={`p-3 rounded-lg bg-gradient-to-br ${getTierColor(achievement.tier)} border-2`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-base font-bold truncate">{achievement.name}</div>
                        <Badge variant="outline" className={getTierBadge(achievement.tier)}>
                          {achievement.tier}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground line-clamp-2">
                        {achievement.description}
                      </div>
                      {achievement.progress !== undefined && achievement.progress < achievement.requirement_value && (
                        <div className="mt-2">
                          <Progress 
                            value={(achievement.progress / achievement.requirement_value) * 100} 
                            className="h-2"
                          />
                          <div className="text-sm text-muted-foreground mt-1">
                            {achievement.progress} / {achievement.requirement_value}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <Crown className="w-5 h-5 text-primary" />
                      <div className="text-sm font-semibold text-primary">
                        +{achievement.points}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {totalPoints > 0 && (
          <div className="text-xs text-center text-muted-foreground pt-2 border-t">
            Keep building your wellness practice to unlock more achievements
          </div>
        )}
      </CardContent>
    </Card>
  );
}
